import React, { useState, useEffect } from 'react';
import "../pages/css/DashBoard.css";
import Header from '../pages/components/HeaderUser';
import SideBar from '../pages/components/SidebarUser';
import UserVerificationUser from '../pages/components/UserVerificationUser';
import { useNavigate } from "react-router-dom";
import { getDatabase, ref, get, push, set, update } from "firebase/database";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import Swal from 'sweetalert2';
import { FaSpinner } from "react-icons/fa";

function Withdraw() {
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [coins, setCoins] = useState({});
    const [prices, setPrices] = useState({});
    const [upiId, setUpiId] = useState("");
    const [selectedCoin, setSelectedCoin] = useState("");
    const [withdrawAmount, setWithdrawAmount] = useState("");
    const [loading, setLoading] = useState(true);
    const [uid, setUid] = useState(null);

    const auth = getAuth();
    const db = getDatabase();
    const navigate = useNavigate();
    UserVerificationUser();

    const toggleSidebar = () => setIsSidebarOpen(prev => !prev);

    // ✅ Listen to auth state properly
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                setUid(user.uid);
            } else {
                setUid(null);
                setCoins({});
                setLoading(false);
            }
        });
        return () => unsubscribe();
    }, [auth]);

    // Fetch user coins once UID is available
    useEffect(() => {
        if (!uid) return;

        const fetchCoins = async () => {
            try {
                const snapshot = await get(ref(db, `blackstone-users/${uid}/buyedCoins`));
                if (snapshot.exists()) {
                    const coinData = snapshot.val();
                    setCoins(coinData);
                    setSelectedCoin(Object.keys(coinData)[0]); // default first coin
                } else {
                    setCoins({});
                }
            } catch (err) {
                console.error("Error fetching coins:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchCoins();
    }, [uid]);

    const Loader = () => (
        <div style={{
            height: '100vh',
            width: '100%',
            backgroundColor: '#fff',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            position: 'fixed',
            top: 0,
            left: 0
        }}>
            <FaSpinner style={{
                fontSize: '3rem',
                color: '#275E76',
                animation: 'spin 1s linear infinite'
            }} />
            <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
        </div>
    );

    // Fetch live prices
    const fetchPrices = async (coinIds) => {
        if (!coinIds || coinIds.length === 0) return;
        try {
            const resp = await fetch(
                `https://api.coingecko.com/api/v3/simple/price?ids=${coinIds.join(",")}&vs_currencies=inr`
            );
            const data = await resp.json();
            setPrices(data);
        } catch (err) {
            console.error("Error fetching prices:", err);
        }
    };

    useEffect(() => {
        const coinIds = Object.keys(coins);
        if (coinIds.length > 0) {
            fetchPrices(coinIds);
            const interval = setInterval(() => fetchPrices(coinIds), 10000);
            return () => clearInterval(interval);
        }
    }, [coins]);

    // ✅ Handle Withdraw with 10% deduction
    const handleWithdraw = async () => {
        if (!upiId) {
            Swal.fire("❌ Error", "Please enter your UPI ID", "error");
            return;
        }
        if (!withdrawAmount || withdrawAmount <= 0) {
            Swal.fire("❌ Error", "Please enter a valid coin amount", "error");
            return;
        }
        if (!selectedCoin || !coins[selectedCoin]) {
            Swal.fire("❌ Error", "Please select a valid coin", "error");
            return;
        }
        if (withdrawAmount > coins[selectedCoin]) {
            Swal.fire("❌ Error", "You don't have enough coins", "error");
            return;
        }

        const coinPriceInr = prices[selectedCoin]?.inr || 0;
        const inrAmount = withdrawAmount * coinPriceInr;

        // 🔹 Apply 10% deduction
        const deduction = withdrawAmount * 0.10;
        const finalWithdrawAmount = withdrawAmount - deduction;
        const finalInrAmount = finalWithdrawAmount * coinPriceInr;

        try {
            const updatedCoins = {
                ...coins,
                [selectedCoin]: coins[selectedCoin] - withdrawAmount
            };
            await update(ref(db, `blackstone-users/${uid}/buyedCoins`), updatedCoins);

            const requestRef = push(ref(db, `blackstone-users/${uid}/withdrawlRequest`));
            await set(requestRef, {
                coin: selectedCoin,
                requestedAmount: withdrawAmount,
                deductedAmount: deduction,
                finalWithdrawAmount,
                inrAmount: finalInrAmount,
                upiId,
                status: "pending",
                requestedAt: new Date().toISOString()
            });

            setCoins(updatedCoins);
            setWithdrawAmount("");
            setUpiId("");

            Swal.fire(
                "✅ Success",
                `Withdrawal request created!\n\nYou will receive ₹${finalInrAmount.toLocaleString()} (after 10% deduction) within 72 hours.`,
                "success"
            );
        } catch (err) {
            console.error("Error creating withdrawal request:", err);
            Swal.fire("❌ Error", "Something went wrong. Try again.", "error");
        }
    };

    const gradients = [
        "linear-gradient(135deg, #667eea, #764ba2)",
        "linear-gradient(135deg, #ff6a00, #ee0979)",
        "linear-gradient(135deg, #56ccf2, #2f80ed)",
        "linear-gradient(135deg, #11998e, #38ef7d)",
        "linear-gradient(135deg, #f7971e, #ffd200)",
        "linear-gradient(135deg, #fc5c7d, #6a82fb)",
    ];

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <Loader />
            </div>
        );
    }

    const coinPriceInr = prices[selectedCoin]?.inr || 0;
    const inrAmount = withdrawAmount * coinPriceInr;
    const deduction = withdrawAmount * 0.10;
    const finalWithdrawAmount = withdrawAmount - deduction;
    const finalInrAmount = finalWithdrawAmount * coinPriceInr;

    return (
        <>
            <div className="d-flex layout-container">
                <SideBar isOpen={isSidebarOpen} />
                <div className="flex-grow-1">
                    <Header onMenuClick={toggleSidebar} />
                    <div className="scrollable-section p-4">
                        <div className="p-4 shadow-md bg-white rounded" style={{ maxWidth: '1300px' }}>
                            {Object.keys(coins).length === 0 ? (
                                <p>You don't have any coins yet.</p>
                            ) : (
                                <div className="row g-4 mb-4">
                                    {Object.keys(coins).map((coin, index) => (
                                        <div className="col-md-4" key={coin}>
                                            <div
                                                className="d-flex flex-column justify-content-center align-items-center text-white p-4"
                                                style={{
                                                    background: gradients[index % gradients.length],
                                                    borderRadius: "20px",
                                                    boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
                                                    height: "180px",
                                                    textAlign: "center",
                                                }}
                                            >
                                                <h5>{coin.toUpperCase()}</h5>
                                                <h6>Balance: {coins[coin].toFixed(6)}</h6>
                                                <p>INR: ₹{((coins[coin] || 0) * (prices[coin]?.inr || 0)).toLocaleString()}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {Object.keys(coins).length > 0 && (
                                <>
                                    <div className="mb-3">
                                        <label className="form-label">Select Coin</label>
                                        <select
                                            className="form-select"
                                            value={selectedCoin}
                                            onChange={(e) => setSelectedCoin(e.target.value)}
                                        >
                                            {Object.keys(coins).map(coin => (
                                                <option key={coin} value={coin}>
                                                    {coin.toUpperCase()} (Balance: {coins[coin].toFixed(6)})
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Enter UPI ID</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={upiId}
                                            onChange={(e) => setUpiId(e.target.value)}
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Amount to Withdraw ({selectedCoin.toUpperCase()})</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={withdrawAmount}
                                            onChange={(e) => setWithdrawAmount(e.target.value)}
                                        />
                                        {withdrawAmount > 0 && (
                                            <div className="mt-2 text-muted" style={{ fontSize: "0.9rem" }}>
                                                <p>INR Equivalent: ₹{inrAmount.toLocaleString()}</p>
                                                <p>10% Deduction: {deduction.toFixed(6)} {selectedCoin.toUpperCase()}</p>
                                                <p><strong>Final You’ll Receive:</strong> ₹{finalInrAmount.toLocaleString()}</p>
                                            </div>
                                        )}
                                    </div>

                                    <button className="btn btn-success w-100" onClick={handleWithdraw}>
                                        Withdraw
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Withdraw;
